import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { emitEvent } from '@faceup/devtools'
import { sharedMessages, useIntl } from '@faceup/localization'
import { useNotification } from '@faceup/ui-base'
import {
  buildUrl,
  DEFAULT_LANGUAGE,
  httpAnalyzerEnabled,
  httpAnalyzerName,
  regionHeaderName,
  type SupportedRegion,
} from '@faceup/utils'
// @ts-expect-error Ignore types https://github.com/jaydenseric/apollo-upload-client/releases/tag/v18.0.0#:~:text=Implemented%20TypeScript%20types%20via%20JSDoc%20comments.
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { print } from 'graphql/language/printer'
import { createClient } from 'graphql-ws'
import { useMemo } from 'react'
import { possibleTypes } from './__generated__/possibleTypes.json'
import { httpAnalyzer } from './httpAnalyzer'
import Auth from './utils/auth'
import { retrieveRegion } from './utils/useRegion'

const suppressedErrors: string[] = [
  'User has no permissions',
  'Report access was already given.',
  'Missing 2FA code',
  'Invalid login credentials',
]

type CustomApolloProviderProps = {
  children?: React.ReactNode
  // When set (e.g. the institution detail pinned to the institution's region), it wins over the
  // selected administration region. When omitted, the region is read live from localStorage so the
  // login flow keeps working without an explicit region.
  region?: SupportedRegion | null
}

const CustomApolloProvider = ({ children, region }: CustomApolloProviderProps) => {
  const { formatMessage } = useIntl()
  const notification = useNotification()
  const resolveRegion = () => region ?? retrieveRegion()
  // inspiration
  // > https://www.apollographql.com/docs/react/advanced/subscriptions.html
  // + file upload https://github.com/jaydenseric/apollo-upload-client#function-createuploadlink
  const httpLink = createUploadLink({
    uri: import.meta.env.VITE_API_URL ?? '',
    credentials: 'include',
    headers: { 'Apollo-Require-Preflight': 'true' },
  })

  const wsLink = new GraphQLWsLink(
    createClient({
      url: buildUrl(
        import.meta.env.VITE_WS_API_URL ?? '',
        resolveRegion() ? { [regionHeaderName]: resolveRegion() as string } : {}
      ),
      lazy: true,
      connectionParams: () => ({
        token: Auth.getJwt() || '',
        language: DEFAULT_LANGUAGE,
        origin: window.location.origin,
        ...(resolveRegion() && { [regionHeaderName]: resolveRegion() }),
      }),
      retryAttempts: Infinity,
    })
  )

  const errorLink = onError(({ networkError, graphQLErrors }) => {
    if (suppressedErrors.includes(graphQLErrors?.[0]?.message ?? '')) {
      return
    }
    if (graphQLErrors?.some(error => error?.message === 'Outdated or corrupted JWT token')) {
      apolloClient.stop()
      apolloClient.clearStore()
      // using then, because onError is not promise friendly :(
      Auth.logout()
      window.location.replace('/')
    }

    // Not reported to Sentry: the backend reports its own failures, and the trace links them
    // to this session. Reporting here turned every rejected login or validation into an issue.
    const errorMessage = networkError?.message ?? graphQLErrors?.[0]?.message ?? ''
    console.error(errorMessage)
    notification.error({
      message: formatMessage(sharedMessages.apiError),
      description: errorMessage,
    })
  })

  const loggingLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        operation.setContext({ start: performance.now() })

        return forward(operation).map(response => {
          const xHttpAnalyzer = operation
            .getContext()
            ['response']?.headers?.get(httpAnalyzerName) as string | null
          const httpAnalyzerStats = xHttpAnalyzer
            ? httpAnalyzer.handleXRequestFaceUpTracingJSON(xHttpAnalyzer)
            : undefined

          emitEvent('cache', operation.getContext()['cache']?.data?.data)

          emitEvent('requests', {
            name: operation.operationName,
            query: print(operation.query),
            variables: operation.variables,
            response: response as Record<string, unknown>,
            httpAnalyzer: {
              ...(httpAnalyzerStats ?? {}),
              duration: performance.now() - operation.getContext()['start'],
            },
            isError: 'errors' in response,
            source: 'gql',
          })

          return response
        })
      }),
    []
  )

  const authLink = setContext((_, { headers }) => {
    const token = Auth.getJwt()

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        ...(resolveRegion() && { [regionHeaderName]: resolveRegion() }),
        ...(httpAnalyzer.getIsEnabled() && { [httpAnalyzerEnabled]: 'true' }),
      },
    }
  })

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query)

      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    loggingLink.concat(errorLink.concat(authLink.concat(httpLink)))
  )

  const apolloClient = new ApolloClient({
    link,
    cache: new InMemoryCache({
      possibleTypes,
    }),
  })

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

export default CustomApolloProvider
